/**
 * HTTP-клиент для портального API.
 * Все эндпойнты Laravel-бэкенда — здесь. Возвращает payload.data или бросает Error.
 */

export const BASE_URL = (window.__API_BASE_URL__ ?? '/api').replace(/\/$/, '');

const TOKEN_KEY = 'auth_token';
const REFRESH_KEY = 'refresh_token';
let authToken = localStorage.getItem(TOKEN_KEY);
let refreshToken = localStorage.getItem(REFRESH_KEY);

function buildHeaders(headers = {}) {
  const result = { 'Content-Type': 'application/json', ...headers };
  if (authToken) result['Authorization'] = `Bearer ${authToken}`;
  return result;
}

export function setAuthToken(token) {
  authToken = token;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function setRefreshToken(token) {
  refreshToken = token;
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
}

/** Полный сброс сессии (оба токена). */
export function clearSession() {
  setAuthToken(null);
  setRefreshToken(null);
}

export function getAuthToken() {
  return authToken;
}

export function isAuthenticated() {
  return Boolean(authToken);
}

// Колбэк «сессия окончательно недействительна» — приложение показывает экран входа.
let onUnauthorizedCb = null;
export function onUnauthorized(cb) {
  onUnauthorizedCb = cb;
}

// Обмен refresh → новый access. Одно обновление на всех: параллельные запросы,
// поймавшие 401, ждут общий промис, чтобы не дёргать /auth/refresh пачкой.
let refreshPromise = null;
function refreshAccessToken() {
  if (!refreshToken) return Promise.resolve(false);
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${BASE_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${refreshToken}` },
        });
        const payload = await res.json().catch(() => null);
        if (!res.ok || !payload?.success || !payload?.data?.token) return false;
        setAuthToken(payload.data.token);
        // Сервер присылает новый refresh только когда старый близок к концу.
        if (payload.data.refresh_token) setRefreshToken(payload.data.refresh_token);
        return true;
      } catch {
        return false;
      }
    })();
    refreshPromise.finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Маршруты, где 401 — это законный ответ (неверный пароль и т.п.), а не «протух токен».
const NO_REFRESH_PATHS = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/refresh'];

async function request(path, options = {}, retried = false) {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    credentials: 'include',
    ...options,
    headers: buildHeaders(options.headers),
  });

  // Бесшовный перевод со старой схемы: сервер мог выдать новую пару токенов
  // прямо в заголовках ответа — сохраняем её, повторный вход не нужен.
  const migratedAccess = response.headers.get('X-New-Access-Token');
  if (migratedAccess) {
    setAuthToken(migratedAccess);
    const migratedRefresh = response.headers.get('X-New-Refresh-Token');
    if (migratedRefresh) setRefreshToken(migratedRefresh);
  }

  // Access-токен истёк: молча меняем его по refresh-токену и повторяем запрос —
  // пользователь ничего не замечает. Если refresh тоже мёртв — на экран входа.
  if (response.status === 401 && !retried && !NO_REFRESH_PATHS.some((p) => path.startsWith(p))) {
    if (await refreshAccessToken()) return request(path, options, true);

    clearSession();
    onUnauthorizedCb?.();
    const err = new Error('Сессия истекла, войдите заново');
    err.status = 401;
    throw err;
  }

  const payload = await response.json().catch(() => ({
    success: false,
    errors: ['Некорректный ответ сервера'],
  }));

  if (!payload.success) {
    const err = new Error(payload.errors?.join(', ') ?? 'Не удалось выполнить запрос');
    err.payload = payload;
    err.status = response.status;
    throw err;
  }
  return payload.data;
}

// ===== Auth =====
export async function login(email, password) {
  const data = await request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (data.token) setAuthToken(data.token);
  if (data.refresh_token) setRefreshToken(data.refresh_token);
  return data;
}

export async function register(payload) {
  return request('/auth/register', { method: 'POST', body: JSON.stringify(payload) });
}

export async function forgotPassword(email) {
  return request('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function logout() {
  clearSession();
}

// ===== Partner / Dashboard =====
export const fetchDashboard = () => request('/partner');
export const updateSettings = (body) =>
  request('/partner/settings', { method: 'PUT', body: JSON.stringify(body) });
export const addAddress = (address) =>
  request('/partner/addresses', { method: 'POST', body: JSON.stringify({ address }) });
export const deleteAddress = (id) =>
  request(`/partner/addresses/${id}`, { method: 'DELETE' });
export const fetchNews = (region) => {
  const search = region ? `?region=${encodeURIComponent(region)}` : '';
  return request(`/partner/news${search}`);
};

// ===== Orders =====
export const fetchOrders = (params = {}) => {
  const search = new URLSearchParams(params).toString();
  return request(`/orders${search ? `?${search}` : ''}`);
};
export const fetchOrderDetails = (orderId) =>
  request(`/orders/${encodeURIComponent(orderId)}`);
export const createOrder = (payload) =>
  request('/orders', { method: 'POST', body: JSON.stringify(payload) });
export const cancelOrder = (orderId, reason = null) =>
  request(`/orders/${encodeURIComponent(orderId)}/cancel`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
export const createOrderChangeRequest = (orderId, type, details = null) =>
  request(`/orders/${encodeURIComponent(orderId)}/change-request`, {
    method: 'POST',
    body: JSON.stringify({ type, details }),
  });
export const orderDocumentUrl = (orderId, type) =>
  `/orders/${encodeURIComponent(orderId)}/document/${encodeURIComponent(type)}`;

// ===== Push (Web Push подписки) =====
export const fetchVapidKey = () => request('/push/vapid-public-key');
export const fetchPushStatus = () => request('/push/status');
export const subscribePush = (subscription) =>
  request('/push/subscribe', { method: 'POST', body: JSON.stringify(subscription) });
export const unsubscribePush = (endpoint) =>
  request('/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) });
export const sendTestPush = () => request('/push/test', { method: 'POST' });

// ===== Catalog =====
export const fetchManufacturers = () => request('/catalog/manufacturers');
export const fetchCategories = () => request('/catalog/categories');
export const searchProducts = (payload) =>
  request('/catalog/search', { method: 'POST', body: JSON.stringify(payload) });
export const fetchProduct = (sku) => request(`/catalog/items/${encodeURIComponent(sku)}`);
export const fetchProductDetails = fetchProduct;

/**
 * Скачать PDF документа заказа (счёт/УПД). Возвращает Blob.
 */
export async function downloadOrderDocument(orderId, type) {
  const response = await fetch(BASE_URL + orderDocumentUrl(orderId, type), {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  });
  if (!response.ok) throw new Error('Не удалось скачать документ');
  return response.blob();
}

/**
 * Лог клиентских ошибок на сервер (best-effort, без await на стороне вызова).
 */
export function reportClientError(payload) {
  return fetch(`${BASE_URL}/log/client-error`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

/**
 * URL для абсолютного пути картинки товара (для тега <img>).
 */
export function absoluteImageUrl(relPath) {
  if (!relPath) return null;
  if (relPath.startsWith('http')) return relPath;
  return BASE_URL + relPath;
}
